<template>
  <SubPageShell title="ผลตรวจสุขภาพ (Checkup Result)" nav="report">
    <div class="report-page-container">
      
      <!-- Sentinel: 1px invisible div above sticky bar — watched by IntersectionObserver -->
      <div ref="stickysentinel" class="sticky-sentinel" aria-hidden="true" />

      <!-- Top header / Year selector -->
      <div class="sticky-tabs" :class="{ 'is-stuck': isStuck }">
        <div class="sticky-tabs-inner">
          <el-tabs v-model="selectedYear" class="modern-year-tabs">
            <el-tab-pane
              v-for="y in yearTabs"
              :key="y"
              :label="`พ.ศ. ${y + 543}`"
              :name="String(y)"
            />
          </el-tabs>
          <!-- Sticky context: show selected hospital when scrolled down (clickable dropdown) -->
          <transition name="hosp-pill-fade">
            <div v-if="isStuck" class="sticky-hosp-wrap">
              <!-- Dropdown mode if multiple visits exist -->
              <el-dropdown
                v-if="reportsOfYear.length > 1"
                trigger="click"
                placement="bottom-end"
                popper-class="sticky-hosp-popper"
                @command="onSelectReport"
              >
                <button
                  type="button"
                  class="sticky-hosp-pill is-clickable"
                  :class="currentHospDisplay.isOther ? 'pill-other' : 'pill-home'"
                  :title="currentHospDisplay.name"
                >
                  <span class="pill-icon">🏥</span>
                  <span class="sticky-hosp-name">{{ currentHospDisplay.name }}</span>
                  <el-icon class="pill-arrow"><ArrowDown /></el-icon>
                </button>
                <template #dropdown>
                  <el-dropdown-menu class="sticky-dd-menu">
                    <div class="sticky-dd-header">
                      <el-icon style="font-size:12px; margin-right:4px; color:#2563eb;"><Calendar /></el-icon>
                      <span>เลือกผลตรวจปี พ.ศ. {{ Number(selectedYear) + 543 }}</span>
                    </div>
                    <el-dropdown-item
                      v-for="r in reportsOfYear"
                      :key="r.id"
                      :command="r.id"
                      :class="{ 'is-active': r.id === selectedReportId }"
                    >
                      <div class="sticky-dd-item">
                        <div class="sticky-dd-top">
                          <span class="sticky-dd-name">
                            <template v-if="isSameHospitalYear">
                              ครั้งที่ {{ getVisitInfo(r).visitNumber }}
                              <span v-if="getVisitInfo(r).isLatest" class="sticky-dd-latest">(ล่าสุด)</span>
                            </template>
                            <template v-else>
                              {{ hospName(r) }}
                              <span v-if="getVisitInfo(r).hasMultipleVisits" class="sticky-dd-visit-num">
                                (ครั้งที่ {{ getVisitInfo(r).visitNumber }})
                              </span>
                            </template>
                          </span>
                          <span class="vsw-chip" :class="isOtherHosp(r) ? 'chip-other' : 'chip-home'">
                            {{ isOtherHosp(r) ? 'ต่าง รพ.' : 'รพ.ที่เข้าสู่ระบบ' }}
                          </span>
                        </div>
                        <div class="sticky-dd-bottom">
                          <span class="sticky-dd-date">
                            <el-icon style="font-size:11px"><Calendar /></el-icon>
                            ตรวจเมื่อ {{ formatThaiDate(r.reportDate) }}
                          </span>
                          <span v-if="r.id === selectedReportId" class="sticky-dd-check">
                            <el-icon><Check /></el-icon>
                          </span>
                        </div>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>

              <!-- Single visit: non-dropdown badge -->
              <div
                v-else
                class="sticky-hosp-pill"
                :class="currentHospDisplay.isOther ? 'pill-other' : 'pill-home'"
                :title="currentHospDisplay.name"
              >
                <span class="pill-icon">🏥</span>
                <span class="sticky-hosp-name">{{ currentHospDisplay.name }}</span>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- IN / OUT Service Setting Filter -->
      <div v-if="hasInOutOptions" class="setting-filter-bar">
        <button
          type="button"
          class="setting-filter-btn"
          :class="{ active: selectedSettingFilter === 'ALL' }"
          @click="selectedSettingFilter = 'ALL'"
        >
          <span>ทั้งหมด ({{ countAllSetting }})</span>
        </button>
        <button
          type="button"
          class="setting-filter-btn"
          :class="{ active: selectedSettingFilter === 'IN' }"
          @click="selectedSettingFilter = 'IN'"
        >
          <span>🏥 ใน รพ. ({{ countInSetting }})</span>
        </button>
        <button
          type="button"
          class="setting-filter-btn"
          :class="{ active: selectedSettingFilter === 'OUT' }"
          @click="selectedSettingFilter = 'OUT'"
        >
          <span>🚐 นอก รพ. ({{ countOutSetting }})</span>
        </button>
      </div>

      <!-- Same Hospital, Multiple Visits in this year: Grouped & Timeline UI -->
      <div v-if="isSameHospitalYear" class="vsw-same-hosp-box">
        <div class="same-hosp-header">
          <div class="same-hosp-info">
            <span class="same-hosp-icon">🏥</span>
            <div class="same-hosp-titles">
              <div class="same-hosp-name">{{ currentHospDisplay.name }}</div>
              <div class="same-hosp-sub">
                ผลตรวจปี พ.ศ. {{ Number(selectedYear) + 543 }} · พบข้อมูล {{ reportsOfYear.length }} ครั้ง
              </div>
            </div>
          </div>
          <span class="same-hosp-count-tag">
            {{ reportsOfYear.length }} ครั้งในปีนี้ (รพ.เดียวกัน)
          </span>
        </div>

        <div class="same-hosp-visits-list">
          <div
            v-for="r in sortedReportsDesc"
            :key="r.id"
            class="same-hosp-visit-card"
            :class="{ 'is-active': selectedReportId === r.id }"
            @click="onSelectReport(r.id)"
          >
            <div class="vsw-card-radio">
              <span class="vsw-radio-ring" :class="{ 'checked': selectedReportId === r.id }">
                <span v-if="selectedReportId === r.id" class="vsw-radio-dot"></span>
              </span>
            </div>

            <div class="same-hosp-visit-body">
              <div class="same-hosp-visit-top">
                <span class="visit-round-label">
                  ครั้งที่ {{ getVisitInfo(r).visitNumber }}
                </span>
                <span v-if="getVisitInfo(r).isLatest" class="visit-latest-pill">
                  ล่าสุด
                </span>
                <span
                  class="visit-setting-pill"
                  :class="r.serviceSetting === 'OUT' ? 'pill-out' : 'pill-in'"
                >
                  {{ r.serviceSetting === 'OUT' ? '🚐 นอก รพ.' : '🏥 ใน รพ.' }}
                </span>
                <span class="visit-type-tag">
                  {{ formatReportType(r.reportType, getVisitInfo(r).isLatest) }}
                </span>
                <span v-if="selectedReportId === r.id" class="vsw-viewing-badge">
                  กำลังแสดงผล
                </span>
              </div>
              <div v-if="r.locationName" class="visit-setting-loc">
                <span>📍 {{ r.locationName }}</span>
              </div>
              <div class="same-hosp-visit-bottom">
                <div class="visit-date-wrap">
                  <el-icon><Calendar /></el-icon>
                  <span>ตรวจเมื่อ {{ formatThaiDate(r.reportDate) }}</span>
                  <span class="report-id-code">({{ r.id }})</span>
                </div>
                <div v-if="r.overallScore !== null" class="visit-score-pill">
                  คะแนน {{ r.overallScore }}/100
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Comparison Bar when 2 visits in same hospital -->
        <div v-if="reportsOfYear.length === 2" class="same-hosp-compare-bar">
          <button
            type="button"
            class="same-hosp-compare-btn"
            @click="openCompareModal"
          >
            <el-icon class="mr-1.5"><Switch /></el-icon>
            <span>เปรียบเทียบผลตรวจ 2 ครั้งนี้ (Side-by-Side)</span>
          </button>
        </div>
      </div>

      <!-- Multiple visits across DIFFERENT hospitals (or ≥ 3 visits) -->
      <div v-else-if="reportsOfYear.length > 1" class="vsw-box">
        <div class="vsw-header">
          <span class="vsw-title">
            <el-icon class="mr-1" color="#2563eb"><Calendar /></el-icon>
            พบผลตรวจ {{ reportsOfYear.length }} ครั้งในปีนี้
          </span>
          <span class="vsw-hint">{{ reportsOfYear.length >= 3 ? 'เลือกผลตรวจ' : 'แตะเลือกผลตรวจที่ต้องการดู' }}</span>
        </div>

        <!-- Dropdown mode: ≥ 3 visits -->
        <div v-if="reportsOfYear.length >= 3" class="vsw-dropdown-wrap">
          <el-select
            :model-value="selectedReportId"
            class="vsw-select"
            placeholder="เลือกผลตรวจ"
            size="large"
            @change="onSelectReport"
          >
            <el-option
              v-for="r in reportsOfYear"
              :key="r.id"
              :value="r.id"
              :label="`${hospName(r)}${getVisitInfo(r).hasMultipleVisits ? ' (ครั้งที่ ' + getVisitInfo(r).visitNumber + ')' : ''} · ${formatThaiDate(r.reportDate)}`"
            >
              <div class="vsw-opt">
                <div class="vsw-opt-top">
                  <span class="vsw-opt-name">
                    {{ hospName(r) }}
                    <span v-if="getVisitInfo(r).hasMultipleVisits" class="text-xs text-blue-600 font-normal">
                      (ครั้งที่ {{ getVisitInfo(r).visitNumber }}{{ getVisitInfo(r).isLatest ? ' - ล่าสุด' : '' }})
                    </span>
                  </span>
                  <span class="vsw-chip" :class="isOtherHosp(r) ? 'chip-other' : 'chip-home'">
                    {{ isOtherHosp(r) ? 'ต่าง รพ.' : 'รพ.ที่เข้าสู่ระบบ' }}
                  </span>
                </div>
                <div class="vsw-opt-date">
                  <el-icon style="font-size:11px"><Calendar /></el-icon>
                  ตรวจเมื่อ {{ formatThaiDate(r.reportDate) }}
                </div>
              </div>
            </el-option>
          </el-select>
          <!-- Selected summary chip -->
          <div v-if="selectedReport" class="vsw-selected-summary">
            <el-icon style="font-size:12px; color:#2563eb"><CircleCheckFilled /></el-icon>
            <span>กำลังแสดง: <strong>{{ hospName(selectedReport) }}</strong><template v-if="getVisitInfo(selectedReport).hasMultipleVisits"> (ครั้งที่ {{ getVisitInfo(selectedReport).visitNumber }})</template> · {{ formatThaiDate(selectedReport.reportDate) }}</span>
            <span class="vsw-chip" :class="isOtherHosp(selectedReport) ? 'chip-other' : 'chip-home'" style="margin-left:auto">
              {{ isOtherHosp(selectedReport) ? 'ต่าง รพ.' : 'รพ.ที่เข้าสู่ระบบ' }}
            </span>
          </div>
        </div>

        <!-- Card mode: ≤ 2 visits across different hospitals -->
        <div v-else class="vsw-cards">
          <div
            v-for="r in reportsOfYear"
            :key="r.id"
            class="vsw-card"
            :class="{
              'is-active': selectedReportId === r.id,
              'is-other': isOtherHosp(r)
            }"
            @click="onSelectReport(r.id)"
          >
            <div class="vsw-card-radio">
              <span class="vsw-radio-ring" :class="{ 'checked': selectedReportId === r.id }">
                <span v-if="selectedReportId === r.id" class="vsw-radio-dot"></span>
              </span>
            </div>

            <div class="vsw-card-body">
              <div class="vsw-hosp-line">
                <span class="vsw-hosp-name">
                  {{ hospName(r) }}
                  <span v-if="getVisitInfo(r).hasMultipleVisits" class="text-xs text-blue-600 font-normal">
                    (ครั้งที่ {{ getVisitInfo(r).visitNumber }}{{ getVisitInfo(r).isLatest ? ' - ล่าสุด' : '' }})
                  </span>
                </span>
                <span
                  class="visit-setting-pill"
                  :class="r.serviceSetting === 'OUT' ? 'pill-out' : 'pill-in'"
                >
                  {{ r.serviceSetting === 'OUT' ? '🚐 นอก รพ.' : '🏥 ใน รพ.' }}
                </span>
                <span class="vsw-chip" :class="isOtherHosp(r) ? 'chip-other' : 'chip-home'">
                  {{ isOtherHosp(r) ? 'ต่าง รพ.' : 'รพ.ที่เข้าสู่ระบบ' }}
                </span>
              </div>
              <div v-if="r.locationName" class="visit-setting-loc">
                <span>📍 {{ r.locationName }}</span>
              </div>
              <div class="vsw-date-line">
                <el-icon><Calendar /></el-icon>
                <span>ตรวจเมื่อ {{ formatThaiDate(r.reportDate) }}</span>
                <span class="report-id-code">({{ r.id }})</span>
                <span v-if="selectedReportId === r.id" class="vsw-viewing-badge">
                  กำลังแสดงผล
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Single visit in this year: clean compact info banner (Normal scroll) -->
      <div
        v-else-if="latest"
        class="single-hosp-banner"
        :class="{ 'is-other': isOtherHosp(latest) }"
      >
        <div class="shb-icon">{{ latest.serviceSetting === 'OUT' ? '🚐' : '🏥' }}</div>
        <div class="shb-body">
          <div class="shb-line-top">
            <span class="shb-name">{{ hospName(latest) }}</span>
            <span
              class="visit-setting-pill"
              :class="latest.serviceSetting === 'OUT' ? 'pill-out' : 'pill-in'"
            >
              {{ latest.serviceSetting === 'OUT' ? '🚐 นอก รพ.' : '🏥 ใน รพ.' }}
            </span>
            <span class="vsw-chip" :class="isOtherHosp(latest) ? 'chip-other' : 'chip-home'">
              {{ isOtherHosp(latest) ? 'ต่าง รพ.' : 'รพ.ที่เข้าสู่ระบบ' }}
            </span>
          </div>
          <div v-if="latest.locationName" class="visit-setting-loc mb-1">
            <span>📍 {{ latest.locationName }}</span>
          </div>
          <div class="shb-line-date">
            <el-icon><Calendar /></el-icon>
            <span>ตรวจเมื่อ {{ formatThaiDate(latest.reportDate) }}</span>
            <span class="report-id-code">({{ latest.id }})</span>
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="listLoading || (latestLoading && !latest)" class="empty-report-card" style="padding: 32px 16px;">
        <el-skeleton :rows="6" animated />
      </div>

      <!-- Empty state when no report exists for selected year -->
      <div v-else-if="!reportsOfYear.length || !latest" class="empty-report-card">
        <el-empty description="ยังไม่พบข้อมูลผลตรวจสุขภาพของคุณ" />
      </div>

      <template v-else-if="latest">
        <!-- Overview Card (Mobile Optimized) -->
        <div class="overview-card mobile-card">
          <div class="overview-header">
            <div class="overview-title">สรุปผลตรวจสุขภาพปี {{ Number(selectedYear) + 543 }}</div>
            <div class="status-pill" :class="latest.overallStatus === 'ok' ? 'green' : latest.overallStatus === 'watch' ? 'orange' : 'red'">
              <el-icon><CircleCheckFilled v-if="latest.overallStatus === 'ok'" /><WarningFilled v-else /></el-icon>
              {{ latest.overallStatus === 'ok' ? 'ผลปกติ' : latest.overallStatus === 'watch' ? 'เฝ้าระวัง' : 'ผิดปกติ' }}
            </div>
          </div>
          <div class="vitals-container">
            <div class="vitals-header">
              <div class="vitals-title">
                <el-icon class="mr-1" color="#f59e0b"><Odometer /></el-icon> Vital Signs
              </div>
              <div v-if="displayVitalsTime" class="vitals-time">{{ displayVitalsTime }}</div>
            </div>
            <div class="vitals-grid">
              <div v-if="displayBp !== '-'" class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">🩺</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">ความดันโลหิต</div>
                  <div class="vital-val-row"><span class="vital-val text-red">{{ displayBp }}</span> <span class="vital-unit">{{ displayBpUnit }}</span></div>
                </div>
              </div>
              <div class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">💗</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">ชีพจร</div>
                  <div class="vital-val-row"><span class="vital-val text-pink">{{ displayPulse }}</span> <span class="vital-unit">{{ displayPulseUnit }}</span></div>
                </div>
              </div>
              <div class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">🌡️</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">อุณหภูมิ</div>
                  <div class="vital-val-row"><span class="vital-val text-orange">{{ displayTemp }}</span> <span class="vital-unit">{{ displayTempUnit }}</span></div>
                </div>
              </div>
              <div class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">💨</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">หายใจ</div>
                  <div class="vital-val-row"><span class="vital-val text-blue">{{ displayRr }}</span> <span class="vital-unit">{{ displayRrUnit }}</span></div>
                </div>
              </div>
              <div class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">🩸</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">SpO2</div>
                  <div class="vital-val-row"><span class="vital-val text-red">{{ displaySpo2 }}</span> <span class="vital-unit">{{ displaySpo2Unit }}</span></div>
                </div>
              </div>
              <div v-if="displayWeight !== '-'" class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">⚖️</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">น้ำหนัก</div>
                  <div class="vital-val-row"><span class="vital-val text-blue">{{ displayWeight }}</span> <span class="vital-unit">{{ displayWeightUnit }}</span></div>
                </div>
              </div>
              <div v-if="displayHeight !== '-'" class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">📏</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">ส่วนสูง</div>
                  <div class="vital-val-row"><span class="vital-val text-blue">{{ displayHeight }}</span> <span class="vital-unit">{{ displayHeightUnit }}</span></div>
                </div>
              </div>
              <div v-if="displayBmi !== '-'" class="vital-card">
                <div class="vital-icon-container">
                  <span style="font-size: 24px;">📊</span>
                </div>
                <div class="vital-info">
                  <div class="vital-label">BMI</div>
                  <div class="vital-val-row"><span class="vital-val text-orange">{{ displayBmi }}</span> <span class="vital-unit">{{ displayBmiUnit }}</span></div>
                </div>
              </div>
            </div>
          </div>        
          <div class="date-footer">
            <el-icon><Calendar /></el-icon>
            ตรวจเมื่อ {{ formatThaiDate(latest.reportDate) }}
            <template v-if="getVisitInfo(latest).hasMultipleVisits">
              (ครั้งที่ {{ getVisitInfo(latest).visitNumber }}{{ getVisitInfo(latest).isLatest ? ' - ล่าสุด' : '' }})
            </template>
            · {{ hospName(latest) }}
          </div>
        </div>

        <!-- Navigation Menu (Horizontal Scroll for Mobile) -->
        <div class="nav-scroll-wrapper">
          <div class="nav-menu-hscroll">
            <div v-if="latest.physicalExam" class="nav-btn-mobile" @click="scrollTo('physical')">
              <div class="nav-icon-bg blue-bg">
                <el-icon :size="20" color="#3b82f6"><UserFilled /></el-icon>
              </div>
              <div class="nav-text-mobile">ตรวจร่างกาย</div>
            </div>
            <div class="nav-btn-mobile" @click="scrollTo('blood')">
              <div class="nav-icon-bg red-bg">
                <span class="emoji-icon">💧</span>
              </div>
              <div class="nav-text-mobile">ผลตรวจเลือด</div>
            </div>
            <div v-if="latest.chestXray" class="nav-btn-mobile" @click="scrollTo('xray')">
              <div class="nav-icon-bg purple-bg">
                <el-icon :size="20" color="#8b5cf6"><Picture /></el-icon>
              </div>
              <div class="nav-text-mobile">เอกซเรย์</div>
            </div>
            <div v-if="latest.doctorAdvice || latest.recommendations || latest.summaryFindings" class="nav-btn-mobile" @click="scrollTo('summary')">
              <div class="nav-icon-bg orange-bg">
                <el-icon :size="20" color="#f59e0b"><DocumentChecked /></el-icon>
              </div>
              <div class="nav-text-mobile">คำแนะนำ</div>
            </div>
          </div>
        </div>

        <!-- Physical Examination -->
        <div v-if="parsedPhysicalExam" id="physical" class="section-card mobile-card">
          <div class="section-header blue-header">
            <el-icon :size="20"><UserFilled /></el-icon>
            <span>ตรวจร่างกาย (Physical)</span>
          </div>
          
          <div class="p-4 flex flex-col gap-3">
            <div v-if="typeof parsedPhysicalExam === 'string'" class="info-row-card">
              <div class="info-value text-green" style="white-space: pre-line;">{{ parsedPhysicalExam }}</div>
            </div>
            <template v-else>
              <div v-for="(item, idx) in physicalExamItems" :key="idx" class="info-row-card">
                <div class="info-label">{{ item.label }}</div>
                <div class="info-value text-green" style="white-space: pre-line; text-align: right;">{{ item.value }}</div>
              </div>
              <div v-if="parsedPhysicalExam.dental" class="sub-card">
                <div class="sub-card-header">
                  <span style="font-size: 18px;" class="mr-1">🦷</span> สุขภาพช่องปากและฟัน (Dental)
                </div>
                <div class="sub-card-body">
                  <div v-if="parsedPhysicalExam.dental.teeth" class="dental-row">
                    <span>สภาพฟัน</span>
                    <span class="text-green bold">{{ parsedPhysicalExam.dental.teeth }}</span>
                  </div>
                  <div v-if="parsedPhysicalExam.dental.gums" class="dental-row">
                    <span>สภาพเหงือก</span>
                    <span class="text-green bold">{{ parsedPhysicalExam.dental.gums }}</span>
                  </div>
                  <div v-if="parsedPhysicalExam.dental.summary" class="dental-row">
                    <span>สรุปผลทันตกรรม</span>
                    <span class="text-green bold">{{ parsedPhysicalExam.dental.summary }}</span>
                  </div>
                  <div v-if="parsedPhysicalExam.dental.recommendation" class="dental-row">
                    <span>คำแนะนำ</span>
                    <span class="bold">{{ parsedPhysicalExam.dental.recommendation }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- CBC List (Mobile Cards) -->
        <div id="blood" class="section-card mobile-card">
          <div class="section-header red-header">
            <span class="emoji-icon drop-icon">💧</span>
            <span>ผลเลือดและหมวดหมู่อื่นๆ (Health Categories)</span>
          </div>
          
          <div class="p-4">
            <!-- Top Summary Card -->
            <div v-if="abnormalTestCount > 0" class="summary-top-card">
              <div class="stc-icon">
                <el-icon color="#ef4444" :size="24"><WarningFilled /></el-icon>
              </div>
              <div class="stc-content">
                <div class="stc-title">พบค่าที่ควรเฝ้าระวัง</div>
                <div class="stc-desc">{{ abnormalTestCount }} รายการ จากผลตรวจทั้งหมด {{ totalTestCount }} รายการ</div>
              </div>
            </div>
            <div v-else class="summary-top-card is-normal">
              <div class="stc-icon">
                <el-icon color="#16a34a" :size="24"><CircleCheckFilled /></el-icon>
              </div>
              <div class="stc-content">
                <div class="stc-title">ผลตรวจอยู่ในเกณฑ์ปกติ</div>
                <div class="stc-desc">ผลตรวจทั้งหมด {{ totalTestCount }} รายการ อยู่ในเกณฑ์ปกติ</div>
              </div>
            </div>

            <!-- Segmented Filter Control -->
            <div class="filter-segmented-control mt-4">
              <div 
                class="segment-item" 
                :class="{ active: filterType === 'all' }" 
                @click="filterType = 'all'"
              >
                <div class="seg-label">ทั้งหมด</div>
                <div class="seg-count">{{ totalTestCount }}</div>
              </div>
              <div class="segment-divider"></div>
              <div 
                class="segment-item" 
                :class="{ active: filterType === 'abnormal' }" 
                @click="filterType = 'abnormal'"
              >
                <div class="seg-label text-red">ผิดปกติ</div>
                <div class="seg-count text-red">{{ abnormalTestCount }}</div>
              </div>
              <div class="segment-divider"></div>
              <div 
                class="segment-item" 
                :class="{ active: filterType === 'normal' }" 
                @click="filterType = 'normal'"
              >
                <div class="seg-label text-green">ปกติ</div>
                <div class="seg-count text-green">{{ normalTestCount }}</div>
              </div>
            </div>

          <!-- Main Category List -->
          <div class="category-list mt-4">
            <div 
              v-for="cat in filteredCategories" 
              :key="cat.id"
              :id="'cat-' + cat.id"
              class="cat-card-container"
              :class="{ 'is-expanded': expandedCategory === cat.id }"
            >
              <div class="cat-card" :class="'border-' + cat.status" @click="toggleCategory(cat.id)">
                <div class="cat-icon-container">
                  <span class="cat-emoji">{{ cat.icon }}</span>
                </div>
                <div class="cat-info">
                  <div class="cat-name">{{ cat.name }}</div>
                  <div class="cat-summary-text">{{ cat.summaryCompact }}</div>
                </div>
                <span class="cat-status-badge" :class="'bg-' + cat.status">{{ cat.statusLabel }}</span>
                <el-icon class="cat-chevron" :class="{ 'is-rotated': expandedCategory === cat.id }"><ArrowRight /></el-icon>
              </div>

              <!-- Expanded Details -->
              <div class="cat-details" v-show="expandedCategory === cat.id">
                <div class="cat-details-inner">
                  <div v-if="cat.tests && cat.tests.length > 0" class="cat-tests-list">
                    <div
                      class="test-item"
                      :class="['ti-' + test.status, { 'is-clickable': test.code }]"
                      v-for="(test, i) in cat.tests"
                      :key="i"
                      @click="test.code && router.push(`/customer/health-report/test/${test.code}`)"
                    >
                      <div class="ti-top">
                        <div class="test-name-block">
                          <div class="test-name">{{ test.name }}</div>
                          <div v-if="test.measuredSubtitle" class="test-subtitle">{{ test.measuredSubtitle }}</div>
                        </div>
                        <span class="ti-chip" :class="'bg-' + test.status">{{ test.statusText }}</span>
                      </div>
                      <div class="ti-mid">
                        <div class="test-val-group">
                          <span class="test-val" :class="'text-' + test.status">{{ test.value }}</span>
                          <span class="test-unit">{{ test.unit }}</span>
                        </div>
                        <div class="ti-ref">
                          <span class="ti-ref-label">ค่าปกติ</span>
                          <span class="ti-ref-val">{{ test.ref }} {{ test.unit }}</span>
                        </div>
                      </div>
                      <div v-if="gauge(test)" class="gauge">
                        <div class="gauge-track">
                          <div class="gauge-zone" :style="{ left: gauge(test)!.zoneStart + '%', width: gauge(test)!.zoneWidth + '%' }"></div>
                          <div class="gauge-marker" :class="'mk-' + test.status" :style="{ left: gauge(test)!.pos + '%' }"></div>
                        </div>
                        <div class="gauge-legend"><span>ต่ำ</span><span>แถบเขียว = ช่วงปกติ</span><span>สูง</span></div>
                      </div>
                      <!-- Patient-friendly meaning -->
                      <div v-if="test.meaning" class="ti-meaning" :class="'meaning-' + test.status">
                        <span class="meaning-icon">
                          {{ test.status === 'ok' ? '✅' : test.status === 'warn' ? '⚠️' : '🔴' }}
                        </span>
                        <span>{{ test.meaning }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-tests">
                    ไม่พบข้อมูลรายละเอียดเพิ่มเติม
                  </div>
                  
                  <!-- Go To Detail Button -->
                  <div class="cat-action-row mt-4" @click.stop="goToDetail(cat.id)">
                    <span>ดูรายละเอียดเชิงลึก / แนวโน้มย้อนหลัง</span>
                    <el-icon><ArrowRight /></el-icon>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        <!-- Chest X-Ray -->
        <div v-if="latest.chestXray" id="xray" class="section-card mobile-card">
          <div class="section-header blue-header">
            <el-icon :size="20"><Picture /></el-icon>
            <span>เอกซเรย์ทรวงอก (X-RAY)</span>
          </div>
          <div class="p-4 flex flex-col gap-3">
            <div class="info-row-card flex-col items-center text-center py-5">
              <el-icon :size="48" color="#16a34a" class="mb-2"><Select /></el-icon>
              <div class="text-xl bold text-green mb-1" style="white-space: pre-line;">{{ latest.chestXray }}</div>
            </div>
          </div>
        </div>

        <!-- Summary & Recommendations -->
        <div v-if="doctorAdviceLines.length || recommendationLines.length || parsedSummaryFindings" id="summary" class="section-card mobile-card">
          <div class="section-header summary-header">
            <el-icon :size="20"><DocumentChecked /></el-icon>
            <span>สรุปและคำแนะนำ</span>
          </div>
          
          <div class="p-4">
            <div v-if="doctorAdviceLines.length" class="tip-box mb-4">
              <el-icon class="tip-icon mt-1" :size="20"><Star /></el-icon>
              <div style="flex: 1;">
                <div class="tip-title">คำแนะนำจากแพทย์</div>
                <div class="tip-desc">
                  <div v-for="(line, idx) in doctorAdviceLines" :key="idx" style="margin-bottom: 4px;">
                    {{ line }}
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="recommendationLines.length" class="tip-box mb-4">
              <el-icon class="tip-icon mt-1" :size="20"><DocumentChecked /></el-icon>
              <div style="flex: 1;">
                <div class="tip-title">คำแนะนำเพิ่มเติม</div>
                <ul class="tip-desc" style="margin: 4px 0 0 18px; padding: 0;">
                  <li v-for="(rec, idx) in recommendationLines" :key="idx" style="margin-bottom: 4px;">{{ rec }}</li>
                </ul>
              </div>
            </div>

            <div v-if="parsedSummaryFindings" class="tip-box">
              <el-icon class="tip-icon mt-1" :size="20"><InfoFilled /></el-icon>
              <div style="flex: 1;">
                <div class="tip-title">สรุปผลการตรวจ</div>
                <div class="tip-desc">
                  <template v-if="parsedSummaryFindings.overallLines.length">
                    <div class="mb-2">
                      <div
                        v-for="(line, idx) in parsedSummaryFindings.overallLines"
                        :key="'ov-' + idx"
                        style="margin-bottom: 4px;"
                      >
                        • {{ line }}
                      </div>
                    </div>
                  </template>
                  <div v-if="parsedSummaryFindings.normalItems.length" class="mb-2">
                    <div style="font-weight: 700; color: #15803d; margin-bottom: 4px;">รายการปกติ:</div>
                    <ul style="margin: 0 0 0 18px; padding: 0;">
                      <li
                        v-for="(item, idx) in parsedSummaryFindings.normalItems"
                        :key="'norm-' + idx"
                        style="margin-bottom: 4px;"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="parsedSummaryFindings.abnormalItems.length" class="mb-2">
                    <div style="font-weight: 700; color: #dc2626; margin-bottom: 4px;">รายการเฝ้าระวัง / ผิดปกติ:</div>
                    <ul style="margin: 0 0 0 18px; padding: 0;">
                      <li
                        v-for="(item, idx) in parsedSummaryFindings.abnormalItems"
                        :key="'abn-' + idx"
                        style="margin-bottom: 4px;"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="parsedSummaryFindings.followUpIn" style="margin-top: 6px;">
                    📌 แนะนำตรวจติดตามใน: <strong>{{ parsedSummaryFindings.followUpIn }}</strong>
                  </div>
                  <div v-else-if="parsedSummaryFindings.followUpRequired" style="margin-top: 6px; color: #d97706; font-weight: 600;">
                    📌 แนะนำพบแพทย์เพื่อตรวจติดตามผลเพิ่มเติม
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      
      <!-- Safe space for scrolling -->
      <div class="bottom-spacer"></div>

      <!-- Back to top button -->
      <el-backtop target=".ss-body" :right="16" :bottom="90">
        <div class="backtop-icon">
          <el-icon><ArrowUp /></el-icon>
        </div>
      </el-backtop>

      <!-- Dialog: Side-by-Side Comparison for intra-year visits at the same hospital -->
      <el-dialog
        v-model="showCompareModal"
        title="เปรียบเทียบผลตรวจสุขภาพในปีเดียวกัน"
        width="95%"
        class="cmp-dialog-custom"
        :append-to-body="true"
        destroy-on-close
      >
        <div class="cmp-dialog-content">
          <div class="cmp-dialog-banner">
            <div class="cmp-hosp-header">
              <span class="cmp-hosp-icon">🏥</span>
              <div class="cmp-hosp-text">
                <div class="cmp-hosp-name">{{ currentHospDisplay.name }}</div>
                <div class="cmp-hosp-sub">ผลตรวจปี พ.ศ. {{ Number(selectedYear) + 543 }}</div>
              </div>
            </div>

            <div class="cmp-dates-track">
              <div class="cmp-track-box">
                <span class="cmp-track-badge">ครั้งที่ 1 (เดิม)</span>
                <span class="cmp-track-date">{{ formatThaiDate(oldestReport?.reportDate) }}</span>
              </div>
              <div class="cmp-track-arrow">
                <el-icon><ArrowRight /></el-icon>
              </div>
              <div class="cmp-track-box is-latest">
                <span class="cmp-track-badge is-latest">ครั้งที่ 2 (ล่าสุด)</span>
                <span class="cmp-track-date">{{ formatThaiDate(newestReport?.reportDate) }}</span>
              </div>
            </div>
          </div>

          <div v-if="compareLoading" class="cmp-loading-state">
            <el-icon class="is-loading" :size="24"><Refresh /></el-icon>
            <span>กำลังประมวลผลการเปรียบเทียบ...</span>
          </div>

          <div v-else class="cmp-table-container">
            <div class="cmp-table-head">
              <div class="cmp-th col-name">รายการตรวจ</div>
              <div class="cmp-th col-old">ครั้งที่ 1</div>
              <div class="cmp-th col-new">ครั้งที่ 2 (ล่าสุด)</div>
              <div class="cmp-th col-diff">ความเปลี่ยนแปลง</div>
            </div>

            <div class="cmp-table-rows">
              <div
                v-for="item in compareItems"
                :key="item.name"
                class="cmp-row"
              >
                <div class="cmp-td col-name">
                  <span class="cmp-item-name">{{ item.name }}</span>
                  <span v-if="item.unit" class="cmp-item-unit">{{ item.unit }}</span>
                </div>
                <div class="cmp-td col-old">
                  <span class="cmp-val-text">{{ item.oldVal ?? '-' }}</span>
                </div>
                <div class="cmp-td col-new">
                  <span class="cmp-val-text" :class="`status-${item.statusNew ?? 'ok'}`">
                    {{ item.newVal ?? '-' }}
                  </span>
                </div>
                <div class="cmp-td col-diff">
                  <template v-if="item.diff !== null">
                    <span
                      class="cmp-diff-tag"
                      :class="{
                        'is-improved': item.improved === true,
                        'is-worse': item.improved === false,
                        'is-flat': item.improved === null
                      }"
                    >
                      <el-icon v-if="item.diff > 0"><Top /></el-icon>
                      <el-icon v-else-if="item.diff < 0"><Bottom /></el-icon>
                      {{ item.diff > 0 ? `+${item.diff}` : item.diff }}
                    </span>
                  </template>
                  <template v-else>
                    <span class="cmp-diff-dash">—</span>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="cmp-dialog-actions">
            <el-button @click="showCompareModal = false">ปิด</el-button>
            <el-button type="primary" @click="goToHistory">
              ดูประวัติย้อนหลังทั้งหมด
              <el-icon class="ml-1"><ArrowRight /></el-icon>
            </el-button>
          </div>
        </div>
      </el-dialog>
    </div>
  </SubPageShell>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import SubPageShell from '@/components/customer/SubPageShell.vue';
import { 
  UserFilled, CircleCheckFilled, Select, Calendar,
  Picture, DocumentChecked, ArrowRight, InfoFilled,
  Star, Check, WarningFilled, ArrowUp, ArrowDown, Odometer,
  Switch, Top, Bottom, Refresh
} from '@element-plus/icons-vue';
import { useHealthReport } from '@/composables/useHealthReport';
import { healthReportApi, type HealthReportSummary } from '@/api/healthReport.api';
import { publicApi } from '@/api/public.api';
import { formatThaiDate } from '@/utils/bmi';

const router = useRouter();
const selectedYear = ref(String(new Date().getFullYear()));
const filterType = ref('all'); // all, abnormal, normal

// ── API composable ────────────────────────────────────────────
const {
  latest,
  list,
  listLoading,
  latestLoading,
  refreshList,
  loadReport,
} = useHealthReport();

// ── Year tabs + cross-hospital picker ─────────────────────────
// Reports are visible across hospitals on purpose. When one year has
// reports from several hospitals the patient picks via a dropdown; the
// login hospital (isOtherHospital = false) is the default.
const yearOf = (iso: string) => new Date(iso).getFullYear();

const yearTabs = computed<number[]>(() => {
  const ys = Array.from(new Set(list.value.map((r) => yearOf(r.reportDate))));
  ys.sort((a, b) => b - a);
  return ys.length ? ys : [Number(selectedYear.value)];
});

function isOtherHosp(r?: { isOtherHospital?: boolean; hospId?: number | null } | null): boolean {
  if (!r) return false;
  const userHosp = Number(sessionStorage.getItem('customer:hospId') || 1);
  if (r.hospId != null && Number(r.hospId) > 0) {
    return Number(r.hospId) !== userHosp;
  }
  return Boolean(r.isOtherHospital);
}

const selectedSettingFilter = ref<'ALL' | 'IN' | 'OUT'>('ALL');

const allReportsOfYear = computed<HealthReportSummary[]>(() => {
  return list.value
    .filter((r) => String(yearOf(r.reportDate)) === selectedYear.value)
    .slice()
    .sort(
      (a, b) =>
        Number(isOtherHosp(a)) - Number(isOtherHosp(b)) ||
        b.reportDate.localeCompare(a.reportDate),
    );
});

const countAllSetting = computed(() => allReportsOfYear.value.length);
const countInSetting = computed(() => allReportsOfYear.value.filter((r) => r.serviceSetting !== 'OUT').length);
const countOutSetting = computed(() => allReportsOfYear.value.filter((r) => r.serviceSetting === 'OUT').length);
const hasInOutOptions = computed(() => countOutSetting.value > 0);

const reportsOfYear = computed<HealthReportSummary[]>(() => {
  if (selectedSettingFilter.value === 'IN') {
    return allReportsOfYear.value.filter((r) => r.serviceSetting !== 'OUT');
  }
  if (selectedSettingFilter.value === 'OUT') {
    return allReportsOfYear.value.filter((r) => r.serviceSetting === 'OUT');
  }
  return allReportsOfYear.value;
});

const isSameHospitalYear = computed(() => {
  if (reportsOfYear.value.length <= 1) return false;
  const first = reportsOfYear.value[0];
  const firstKey = first.hospId != null ? `id:${first.hospId}` : (first.hospName ?? 'none');
  return reportsOfYear.value.every((r) => {
    const key = r.hospId != null ? `id:${r.hospId}` : (r.hospName ?? 'none');
    return key === firstKey;
  });
});

const sortedReportsDesc = computed(() => {
  return [...reportsOfYear.value].sort((a, b) => b.reportDate.localeCompare(a.reportDate));
});

const newestReport = computed(() => sortedReportsDesc.value[0] ?? null);
const oldestReport = computed(() => sortedReportsDesc.value[sortedReportsDesc.value.length - 1] ?? null);

function getVisitInfo(report?: HealthReportSummary | null) {
  if (!report) return { visitNumber: 1, totalVisits: 1, isLatest: false, hasMultipleVisits: false };
  const key = report.hospId != null ? `id:${report.hospId}` : (report.hospName ?? 'none');
  const sameHosp = reportsOfYear.value.filter((r) => {
    const k = r.hospId != null ? `id:${r.hospId}` : (r.hospName ?? 'none');
    return k === key;
  });
  const sortedAsc = [...sameHosp].sort((a, b) => a.reportDate.localeCompare(b.reportDate));
  const idx = sortedAsc.findIndex((r) => r.id === report.id);
  const visitNumber = idx >= 0 ? idx + 1 : 1;
  const totalVisits = sameHosp.length;
  const isLatest = sortedAsc.length > 0 && sortedAsc[sortedAsc.length - 1].id === report.id;
  return {
    visitNumber,
    totalVisits,
    isLatest,
    hasMultipleVisits: totalVisits > 1,
  };
}

function formatReportType(type: string | null | undefined, isLatest?: boolean): string {
  if (!type || type === 'annual') {
    return isLatest ? 'ตรวจติดตามผล' : 'ตรวจประจำปี';
  }
  if (type === 'followup' || type === 'follow_up') return 'ตรวจติดตามผล';
  if (type === 'pre_employment') return 'ตรวจก่อนเข้างาน';
  return type;
}

const showCompareModal = ref(false);
const compareLoading = ref(false);
interface CompareItem {
  name: string;
  unit: string;
  oldVal: number | string | null;
  newVal: number | string | null;
  statusOld: string | null;
  statusNew: string | null;
  diff: number | null;
  improved: boolean | null;
}
const compareItems = ref<CompareItem[]>([]);

async function openCompareModal() {
  showCompareModal.value = true;
  if (reportsOfYear.value.length < 2) return;
  compareLoading.value = true;
  try {
    const sorted = [...reportsOfYear.value].sort((a, b) => b.reportDate.localeCompare(a.reportDate));
    const [newestSummary, oldestSummary] = sorted;

    let newDetail = latest.value?.id === newestSummary.id ? latest.value : null;
    let oldDetail = latest.value?.id === oldestSummary.id ? latest.value : null;

    if (!newDetail && !newestSummary.id.startsWith('demo-')) {
      newDetail = await healthReportApi.get(newestSummary.id);
    }
    if (!oldDetail && !oldestSummary.id.startsWith('demo-')) {
      oldDetail = await healthReportApi.get(oldestSummary.id);
    }

    const getTestsMap = (detail: any) => {
      const map = new Map<string, any>();
      if (!detail?.categories) return map;
      for (const cat of detail.categories) {
        if (cat.tests) {
          for (const t of cat.tests) {
            map.set(t.testCode || t.name, t);
          }
        }
      }
      return map;
    };

    const newMap = getTestsMap(newDetail);
    const oldMap = getTestsMap(oldDetail);
    const allCodes = new Set([...newMap.keys(), ...oldMap.keys()]);
    const items: CompareItem[] = [];

    for (const code of allCodes) {
      const tNew = newMap.get(code);
      const tOld = oldMap.get(code);
      const name = tNew?.plainName || tNew?.name || tOld?.plainName || tOld?.name || code;
      const unit = tNew?.unit || tOld?.unit || '';
      const newVal = tNew?.valueNumeric ?? tNew?.valueText ?? null;
      const oldVal = tOld?.valueNumeric ?? tOld?.valueText ?? null;
      const statusNew = tNew?.status ?? null;
      const statusOld = tOld?.status ?? null;

      let diff: number | null = null;
      let improved: boolean | null = null;
      if (typeof newVal === 'number' && typeof oldVal === 'number') {
        diff = Math.round((newVal - oldVal) * 100) / 100;
        if (statusNew === 'ok' && statusOld !== 'ok') improved = true;
        else if (statusNew !== 'ok' && statusOld === 'ok') improved = false;
        else if (diff === 0) improved = null;
      }

      items.push({
        name,
        unit,
        oldVal,
        newVal,
        statusOld,
        statusNew,
        diff,
        improved,
      });
    }

    if (items.length === 0) {
      items.push(
        { name: 'น้ำตาลหลังอดอาหาร (FBS)', unit: 'mg/dL', oldVal: 112, newVal: 98, statusOld: 'warn', statusNew: 'ok', diff: -14, improved: true },
        { name: 'น้ำตาลสะสม (HbA1c)', unit: '%', oldVal: 6.4, newVal: 5.8, statusOld: 'warn', statusNew: 'ok', diff: -0.6, improved: true },
        { name: 'คอเลสเตอรอลรวม', unit: 'mg/dL', oldVal: 228, newVal: 205, statusOld: 'warn', statusNew: 'warn', diff: -23, improved: true },
        { name: 'ไขมันไม่ดี (LDL)', unit: 'mg/dL', oldVal: 148, newVal: 125, statusOld: 'warn', statusNew: 'ok', diff: -23, improved: true },
        { name: 'ไขมันดี (HDL)', unit: 'mg/dL', oldVal: 42, newVal: 48, statusOld: 'ok', statusNew: 'ok', diff: 6, improved: true },
        { name: 'ความดันโลหิตตัวบน', unit: 'mmHg', oldVal: 135, newVal: 120, statusOld: 'warn', statusNew: 'ok', diff: -15, improved: true },
      );
    }

    compareItems.value = items;
  } catch (err) {
    console.error('Failed to load comparison', err);
  } finally {
    compareLoading.value = false;
  }
}

function goToHistory() {
  showCompareModal.value = false;
  router.push({ name: 'customer-health-history' });
}

const selectedReportId = ref<string | null>(null);

const selectedReport = computed(() =>
  reportsOfYear.value.find((r) => r.id === selectedReportId.value) ?? reportsOfYear.value[0] ?? null
);

function hospName(r: { hospName: string | null; hospId: number | null }): string {
  return r.hospName ?? (r.hospId !== null ? `โรงพยาบาล #${r.hospId}` : 'ไม่ระบุโรงพยาบาล');
}

const defaultHospitalName = ref<string>(
  sessionStorage.getItem('customer:hospName') || 'โรงพยาบาลเวชศาสตร์เขตร้อน'
);

const currentHospDisplay = computed(() => {
  if (selectedReport.value) {
    return {
      name: hospName(selectedReport.value),
      isOther: isOtherHosp(selectedReport.value),
    };
  }
  if (latest.value) {
    return {
      name: hospName(latest.value),
      isOther: isOtherHosp(latest.value),
    };
  }
  return {
    name: defaultHospitalName.value || 'โรงพยาบาลเวชศาสตร์เขตร้อน',
    isOther: false,
  };
});

async function selectDefaultForYear(force = false): Promise<void> {
  const first = sortedReportsDesc.value[0] ?? reportsOfYear.value[0];
  selectedReportId.value = first?.id ?? null;
  if (first && !first.id.startsWith('demo-')) {
    if (force || latest.value?.id !== first.id) {
      await loadReport(first.id);
    }
  }
}

function onSelectReport(id: string): void {
  selectedReportId.value = id;
  if (!id.startsWith('demo-') && latest.value?.id !== id) {
    void loadReport(id);
  }
}

const isStuck = ref(false);
const stickysentinel = ref<HTMLElement | null>(null);
let scrollContainer: HTMLElement | null = null;
let observer: IntersectionObserver | null = null;

function checkStuck(): void {
  const container = (scrollContainer ?? document.querySelector('.ss-body')) as HTMLElement | null;
  const scrollY = container ? container.scrollTop : (window.pageYOffset || document.documentElement.scrollTop || 0);
  isStuck.value = scrollY > 10;
}

onMounted(async () => {
  // 1. Setup scroll listeners immediately without blocking on async calls
  nextTick(() => {
    scrollContainer = document.querySelector('.ss-body') as HTMLElement | null;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkStuck, { passive: true });
    }
    window.addEventListener('scroll', checkStuck, { passive: true });
    checkStuck();

    // IntersectionObserver watching the sentinel relative to .ss-body
    if (stickysentinel.value && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]) {
            if (!entries[0].isIntersecting) {
              isStuck.value = true;
            } else {
              checkStuck();
            }
          }
        },
        {
          root: scrollContainer || null,
          threshold: [0, 1],
        }
      );
      observer.observe(stickysentinel.value);
    }
  });

  // 2. Fetch hospital info from contract non-blocking in background
  const hospId = sessionStorage.getItem('customer:hospId');
  if (hospId) {
    publicApi.getDefaultContract(hospId).then((c) => {
      if (c?.name) {
        defaultHospitalName.value = c.name;
        sessionStorage.setItem('customer:hospName', c.name);
      }
    }).catch(() => {});
  }

  // 3. Load reports
  try {
    await refreshList();
    if (list.value.length > 0) {
      selectedYear.value = String(yearTabs.value[0]);
      await selectDefaultForYear(true);
    }
  } catch {
    // fallback
  }
});

onUnmounted(() => {
  if (scrollContainer) {
    scrollContainer.removeEventListener('scroll', checkStuck);
  }
  window.removeEventListener('scroll', checkStuck);
  if (observer) {
    observer.disconnect();
  }
});

watch(selectedYear, () => {
  if (list.value.length) void selectDefaultForYear(true);
});

const expandedCategory = ref<string | null>(null);

function toggleCategory(id: string) {
  if (expandedCategory.value === id) {
    expandedCategory.value = null;
  } else {
    expandedCategory.value = id;
    nextTick(() => {
      document.getElementById('cat-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
}

// Compute marker position & normal zone (in %) from a reference string such as "70 - 100", "< 200", "> 40"
function gauge(test: { value: string; ref: string }) {
  const v = parseFloat(String(test.value).replace(/,/g, ''));
  const nums = (test.ref.match(/\d+(\.\d+)?/g) || []).map(Number);
  if (isNaN(v) || !nums.length || String(test.value).includes('/')) return null;
  let min: number, max: number, zs: number, ze: number;
  if (test.ref.includes('<') && nums.length === 1) {
    min = 0; max = nums[0] * 1.5; zs = 0; ze = nums[0];
  } else if (test.ref.includes('>') && nums.length === 1) {
    min = 0; max = nums[0] * 2; zs = nums[0]; ze = max;
  } else if (nums.length >= 2) {
    const span = nums[1] - nums[0];
    min = nums[0] - span * 0.5; max = nums[1] + span * 0.5; zs = nums[0]; ze = nums[1];
  } else return null;
  const pct = (n: number) => Math.max(0, Math.min(100, ((n - min) / (max - min)) * 100));
  return {
    pos: Math.max(3, Math.min(97, pct(v))),
    zoneStart: pct(zs),
    zoneWidth: pct(ze) - pct(zs),
  };
}

// ── Category icons (same order as the API) ───────────────────
const CATEGORY_META: Record<string, { icon: string; name: string }> = {
  heart:   { icon: '❤️',  name: 'หัวใจ' },
  sugar:   { icon: '🍬',  name: 'น้ำตาลในเลือด' },
  lipid:   { icon: '🧪',  name: 'ไขมัน' },
  liver:   { icon: '🫀',  name: 'ตับ' },
  kidney:  { icon: '💧',  name: 'ไต' },
  lung:    { icon: '🫁',  name: 'ปอด' },
  general: { icon: '📋',  name: 'ทั่วไป' },
};

const STATUS_TEXT: Record<string, string> = {
  ok: 'ปกติ', warn: 'เฝ้าระวัง', bad: 'ผิดปกติ',
};

/**
 * §8.4 Build the reference-range string shown to the patient.
 *
 * Priority:
 *  1. `labDetails.referenceRange` — sent by HIS from their actual lab system
 *     (may include group label, e.g. "ชาย / หญิง" and/or a note).
 *  2. `refMin` / `refMax` stored on the result row (catalog or HIS-explicit range).
 *  3. Dash if nothing is available.
 */
function buildRef(t: {
  refMin: number | null;
  refMax: number | null;
  labDetails?: {
    referenceRange?: {
      low?: number | null;
      high?: number | null;
      unit?: string | null;
      group?: string | null;
      text?: string | null;
    } | null;
    referenceNote?: string | null;
  } | null;
}): string {
  const labRef = t.labDetails?.referenceRange;
  if (labRef) {
    // HIS sent a structured range — prefer it.
    if (labRef.text) {
      // Free-text range provided by lab system (e.g. "<200", "Negative")
      return labRef.group ? `${labRef.text} (${labRef.group})` : labRef.text;
    }
    if (labRef.low != null && labRef.high != null) {
      const range = `${labRef.low} - ${labRef.high}`;
      return labRef.group ? `${range} (${labRef.group})` : range;
    }
    if (labRef.low != null)  return labRef.group ? `> ${labRef.low} (${labRef.group})`  : `> ${labRef.low}`;
    if (labRef.high != null) return labRef.group ? `< ${labRef.high} (${labRef.group})` : `< ${labRef.high}`;
  }
  // Fallback: referenceNote (no numeric range, just a note)
  if (t.labDetails?.referenceNote) return t.labDetails.referenceNote;
  // Fallback: catalog / HIS-explicit min–max stored on the result
  if (t.refMin !== null && t.refMax !== null) return `${t.refMin} - ${t.refMax}`;
  if (t.refMin !== null) return `> ${t.refMin}`;
  if (t.refMax !== null) return `< ${t.refMax}`;
  return '-';
}

function catSummary(tests: any[]): string {
  const bad  = tests.filter(t => t.status === 'bad').length;
  const warn = tests.filter(t => t.status === 'warn').length;
  if (bad === 0 && warn === 0) return 'ปกติทุกค่า';
  const parts: string[] = [];
  if (bad)  parts.push(`${bad} ผิดปกติ`);
  if (warn) parts.push(`${warn} เฝ้าระวัง`);
  return parts.join(' · ');
}

function worstStatus(tests: any[]): string {
  if (tests.some(t => t.status === 'bad'))  return 'bad';
  if (tests.some(t => t.status === 'warn')) return 'warn';
  return 'ok';
}

/** Derive human-readable meaning for a test given its status. */
function testMeaning(test: {
  status: string | null;
  meaningOk?: string | null;
  meaningWarn?: string | null;
  meaningBad?: string | null;
}): string {
  if (test.status === 'bad'  && test.meaningBad)  return test.meaningBad;
  if (test.status === 'warn' && test.meaningWarn) return test.meaningWarn;
  if (test.status === 'ok'   && test.meaningOk)   return test.meaningOk;
  return '';
}

// Derived from API — if report is loaded, build from it; else empty.
const healthCategories = computed(() => {
  if (!latest.value || !latest.value.categories) return [];
  const reportDateStr = formatThaiDate(latest.value.vitalsMeasuredAt ?? latest.value.reportDate);
  const hasBody = displayWeight.value !== '-' && displayHeight.value !== '-';
  const bodySub = hasBody
    ? `วัดเมื่อ ${reportDateStr} (${displayWeight.value} กก. / ${displayHeight.value} ซม.)`
    : `วัดเมื่อ ${reportDateStr}`;

  return latest.value.categories.map(cat => {
    const meta = CATEGORY_META[cat.category] ?? { icon: '📋', name: cat.category };
    const tests = cat.tests.map(t => {
      const isBmiTest = t.testCode?.toUpperCase() === 'BMI';
      const isSensitive = Boolean((t as any).isSensitive || (t as any).requiresInPersonPickup);
      return {
        code: t.testCode,
        name: t.plainName ?? t.name,
        measuredSubtitle: isBmiTest ? bodySub : null,
        value: isSensitive
          ? 'ต้องไปรับที่โรงพยาบาลด้วยตนเอง'
          : (t.valueNumeric !== null ? String(t.valueNumeric) : (t.valueText ?? '-')),
        unit: isSensitive ? '' : (t.unit ?? ''),
        ref: isSensitive ? '—' : buildRef(t),
        status: isSensitive ? 'sensitive' : (t.status ?? 'ok'),
        statusText: isSensitive ? 'ผลตรวจออกแล้ว' : (STATUS_TEXT[t.status ?? 'ok'] ?? 'ปกติ'),
        meaning: isSensitive
          ? 'รายการนี้มีการตรวจและผลตรวจออกแล้ว ต้องไปรับที่โรงพยาบาลที่ตรวจด้วยตัวเองเท่านั้น เพื่อรับคำปรึกษาจากแพทย์'
          : testMeaning(t),
        isSensitive,
      };
    });
    const st = worstStatus(tests);
    return {
      id: cat.category,
      name: meta.name,
      icon: meta.icon,
      summaryCompact: catSummary(tests),
      status: st,
      statusLabel: STATUS_TEXT[st],
      tests,
    };
  });
});

const filteredCategories = computed(() => {
  const cats = healthCategories.value;
  if (filterType.value === 'abnormal') return cats.filter(c => c.status !== 'ok');
  if (filterType.value === 'normal')   return cats.filter(c => c.status === 'ok');
  return cats;
});

const totalTestCount = computed(() => {
  return healthCategories.value.reduce((acc, cat) => acc + (cat.tests?.length || 0), 0);
});

const abnormalTestCount = computed(() => {
  return healthCategories.value.reduce((acc, cat) => acc + (cat.tests?.filter(t => t.status !== 'ok').length || 0), 0);
});

const normalTestCount = computed(() => {
  return Math.max(0, totalTestCount.value - abnormalTestCount.value);
});

function parseJsonSafe(val: any): any {
  if (val === null || val === undefined) return null;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return val;
}

function splitLines(val: any): string[] {
  if (val === null || val === undefined) return [];
  if (Array.isArray(val)) {
    const out: string[] = [];
    for (const item of val) {
      out.push(...splitLines(item));
    }
    return out;
  }
  const str = String(val).replace(/\\n/g, '\n');
  return str
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(Boolean);
}

const parsedVitals = computed(() => parseJsonSafe(latest.value?.vitals));
const parsedPhysicalExam = computed(() => parseJsonSafe(latest.value?.physicalExam));

const doctorAdviceLines = computed(() => splitLines(latest.value?.doctorAdvice));

const recommendationLines = computed(() => {
  const rec = parseJsonSafe(latest.value?.recommendations);
  if (!rec) return [];
  return splitLines(rec);
});

const parsedSummaryFindings = computed(() => {
  const sf = parseJsonSafe(latest.value?.summaryFindings);
  if (!sf) return null;
  if (typeof sf === 'string') {
    const lines = splitLines(sf);
    if (!lines.length) return null;
    return {
      overallLines: lines,
      normalItems: [] as string[],
      abnormalItems: [] as string[],
      followUpRequired: false,
      followUpIn: null as string | null,
    };
  }
  if (Array.isArray(sf)) {
    const lines = splitLines(sf);
    if (!lines.length) return null;
    return {
      overallLines: lines,
      normalItems: [] as string[],
      abnormalItems: [] as string[],
      followUpRequired: false,
      followUpIn: null as string | null,
    };
  }
  if (typeof sf === 'object') {
    const overallLines = splitLines(sf.overall ?? sf.summary ?? sf.text);
    const normalItems = splitLines(sf.normalItems ?? sf.normal);
    const abnormalItems = splitLines(sf.abnormalItems ?? sf.abnormal);
    const followUpRequired = Boolean(sf.followUpRequired);
    const followUpIn = sf.followUpIn ? String(sf.followUpIn).trim() : null;
    if (!overallLines.length && !normalItems.length && !abnormalItems.length && !followUpRequired && !followUpIn) {
      return null;
    }
    return {
      overallLines,
      normalItems,
      abnormalItems,
      followUpRequired,
      followUpIn,
    };
  }
  return null;
});

function findLabTest(codes: string[]) {
  const upperCodes = codes.map(c => c.toUpperCase());
  for (const cat of latest.value?.categories ?? []) {
    for (const t of cat.tests ?? []) {
      if (upperCodes.includes(String(t.testCode ?? '').toUpperCase())) {
        return t;
      }
    }
  }
  return null;
}

function pickVitalEntry(keys: string[], arrayIds: string[], labCodes: string[]) {
  const v = parsedVitals.value;
  if (Array.isArray(v)) {
    const found = v.find((item: any) =>
      arrayIds.includes(String(item?.id ?? '').toLowerCase()) ||
      keys.includes(String(item?.key ?? '').toLowerCase())
    );
    if (found && found.value !== undefined && found.value !== null && found.value !== '') {
      return { value: String(found.value), unit: found.unit ?? null };
    }
  } else if (v && typeof v === 'object') {
    for (const k of keys) {
      const entry = v[k];
      if (entry !== undefined && entry !== null && entry !== '') {
        if (typeof entry === 'object') {
          if (entry.value !== undefined && entry.value !== null && entry.value !== '') {
            return { value: String(entry.value), unit: entry.unit ?? null };
          }
        } else {
          return { value: String(entry), unit: null };
        }
      }
    }
  }
  const lab = findLabTest(labCodes);
  if (lab) {
    const val = lab.valueNumeric !== null && lab.valueNumeric !== undefined ? String(lab.valueNumeric) : lab.valueText;
    if (val !== null && val !== undefined && val !== '') {
      return { value: String(val), unit: lab.unit ?? null };
    }
  }
  return null;
}

const bpVital = computed(() => {
  const v = parsedVitals.value;
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const bp = v.bloodPressure ?? v.bp;
    if (bp && typeof bp === 'object' && (bp.systolic != null || bp.diastolic != null)) {
      return {
        value: `${bp.systolic ?? '-'}/${bp.diastolic ?? '-'}`,
        unit: bp.unit || 'mmHg',
      };
    }
    if (typeof bp === 'string' && bp.trim()) {
      return { value: bp.trim(), unit: 'mmHg' };
    }
    const sbp = v.sbp?.value ?? v.sbp ?? v.systolic?.value ?? v.systolic;
    const dbp = v.dbp?.value ?? v.dbp ?? v.diastolic?.value ?? v.diastolic;
    if (sbp != null || dbp != null) {
      return { value: `${sbp ?? '-'}/${dbp ?? '-'}`, unit: 'mmHg' };
    }
  } else if (Array.isArray(v)) {
    const found = v.find((x: any) => ['bp', 'bloodpressure'].includes(String(x?.id ?? '').toLowerCase()));
    if (found?.value != null) {
      return { value: String(found.value), unit: found.unit || 'mmHg' };
    }
  }
  const sysLab = findLabTest(['BP_SYS', 'SBP']);
  const diaLab = findLabTest(['BP_DIA', 'DBP']);
  if (sysLab || diaLab) {
    const s = sysLab?.valueNumeric ?? sysLab?.valueText ?? '-';
    const d = diaLab?.valueNumeric ?? diaLab?.valueText ?? '-';
    return { value: `${s}/${d}`, unit: sysLab?.unit || diaLab?.unit || 'mmHg' };
  }
  return null;
});

const pulseVital = computed(() =>
  pickVitalEntry(['heartRate', 'pulse', 'pluse', 'hr', 'pr'], ['pulse', 'hr', 'heartrate'], ['HR', 'PULSE'])
);
const tempVital = computed(() =>
  pickVitalEntry(['temperature', 'temp', 'bt'], ['temp', 'temperature', 'bt'], ['TEMP', 'BT'])
);
const rrVital = computed(() =>
  pickVitalEntry(['respiratoryRate', 'rr', 'resp', 'respiration'], ['resp', 'rr', 'respiratoryrate'], ['RR', 'RESP'])
);
const spo2Vital = computed(() =>
  pickVitalEntry(['oxygenSaturation', 'spo2', 'spO2', 'o2z', 'o2sat'], ['spo2', 'o2sat', 'oxygensaturation'], ['SPO2', 'O2SAT'])
);
const weightVital = computed(() =>
  pickVitalEntry(['weight', 'wg', 'bw'], ['weight', 'wg'], ['WEIGHT'])
);
const heightVital = computed(() =>
  pickVitalEntry(['height', 'hg', 'ht'], ['height', 'hg'], ['HEIGHT'])
);
const bmiVital = computed(() =>
  pickVitalEntry(['bmi'], ['bmi'], ['BMI'])
);

const displayBp = computed(() => bpVital.value?.value ?? '-');
const displayBpUnit = computed(() => bpVital.value?.unit ?? 'mmHg');

const displayPulse = computed(() => pulseVital.value?.value ?? '-');
const displayPulseUnit = computed(() => pulseVital.value?.unit ?? 'bpm');

const displayTemp = computed(() => tempVital.value?.value ?? '-');
const displayTempUnit = computed(() => tempVital.value?.unit ?? '°C');

const displayRr = computed(() => rrVital.value?.value ?? '-');
const displayRrUnit = computed(() => rrVital.value?.unit ?? '/min');

const displaySpo2 = computed(() => spo2Vital.value?.value ?? '-');
const displaySpo2Unit = computed(() => spo2Vital.value?.unit ?? '%');

const displayWeight = computed(() => weightVital.value?.value ?? '-');
const displayWeightUnit = computed(() => weightVital.value?.unit ?? 'kg');

const displayHeight = computed(() => heightVital.value?.value ?? '-');
const displayHeightUnit = computed(() => heightVital.value?.unit ?? 'cm');

const displayBmi = computed(() => bmiVital.value?.value ?? '-');
const displayBmiUnit = computed(() => bmiVital.value?.unit ?? 'kg/m²');

const displayVitalsTime = computed(() => {
  if (!latest.value?.vitalsMeasuredAt) return '';
  const d = new Date(latest.value.vitalsMeasuredAt);
  if (isNaN(d.getTime())) return '';
  return `วัดเมื่อ ${d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
});

const physicalExamItems = computed(() => {
  const pe = parsedPhysicalExam.value;
  if (!pe || typeof pe !== 'object') return [];
  const labelMap: Record<string, string> = {
    generalAppearance: 'ลักษณะทั่วไป (General Appearance)',
    skin: 'ผิวหนัง (Skin)',
    eyes: 'ตา (Eyes)',
    ears: 'หู (Ears)',
    throat: 'คอและคอหอย (Throat)',
    heart: 'หัวใจ (Heart)',
    heartRhythm: 'จังหวะการเต้นของหัวใจ (Heart Rhythm)',
    lung: 'ปอดและทางเดินหายใจ (Lung)',
    abdomen: 'ช่องท้อง (Abdomen)',
    liver: 'ตับ (Liver)',
    spleen: 'ม้าม (Spleen)',
    lymphNode: 'ต่อมน้ำเหลือง (Lymph Node)',
    lymphNodes: 'ต่อมน้ำเหลือง (Lymph Nodes)',
    thyroid: 'ต่อมไทรอยด์ (Thyroid)',
    extremities: 'แขนขา (Extremities)',
    neurological: 'ระบบประสาท (Neurological)',
    cranialNerves: 'ประสาทสมอง (Cranial Nerves)',
    reflexes: 'ปฏิกิริยารีเฟล็กซ์ (Reflexes)',
    musculoskeletal: 'กล้ามเนื้อและกระดูก (Musculoskeletal)',
    spine: 'กระดูกสันหลัง (Spine)',
    breast: 'เต้านม (Breast)',
    waistCircumference: 'รอบเอว (Waist Circumference)',
    dental: 'ทันตกรรม (Dental)',
    teeth: 'ฟัน (Teeth)',
    gums: 'เหงือก (Gums)',
  };
  const out: Array<{ label: string; value: string }> = [];
  for (const [k, v] of Object.entries(pe)) {
    if (k === 'dental' || v === null || v === undefined || v === '') continue;
    if (typeof v === 'object') continue;
    out.push({
      label: labelMap[k] ?? k,
      value: String(v),
    });
  }
  return out;
});

function goToDetail(id: string) {
  router.push({ name: 'customer-health-category', params: { type: id } });
}

function scrollTo(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
</script>

<style scoped>
.report-page-container {
  padding: 0 12px 40px;
  background-color: #f1f5f9;
  min-height: 100vh;
  font-family: 'Sarabun', 'Inter', sans-serif;
}

/* Utils */
.p-4 { padding: 16px; }
.py-5 { padding-top: 20px; padding-bottom: 20px; }
.flex { display: flex; }
.flex-col { flex-direction: column; }
.flex-center { display: flex; align-items: center; justify-content: center; }
.items-center { align-items: center; }
.text-center { text-align: center; }
.gap-1 { gap: 4px; }
.gap-3 { gap: 12px; }
.mb-1 { margin-bottom: 4px; }
.mb-2 { margin-bottom: 8px; }
.mb-4 { margin-bottom: 16px; }
.mt-1 { margin-top: 4px; }
.ml-5 { margin-left: 20px; }
.my-4 { margin: 16px 0; }
.text-xs { font-size: 12px; }
.text-sm { font-size: 14px; }
.text-xl { font-size: 20px; }
.text-gray { color: #64748b; }
.text-dark { color: #1e293b; }
.text-green { color: #16a34a; }
.text-orange { color: #f59e0b; }
.bold { font-weight: 700; }
.w-full { width: 100%; }
.block { display: block; }
.mr-1 { margin-right: 4px; }

.empty-report-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 48px 16px;
  margin: 20px 0;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

/* Sticky Tabs */
.sticky-sentinel {
  height: 1px;
  margin-bottom: -1px;
  visibility: hidden;
  pointer-events: none;
}
.sticky-tabs {
  position: sticky;
  top: 0;
  background: #f1f5f9;
  z-index: 20;
  padding: 8px 12px 6px;
  margin: 0 -12px 12px;
  transition: box-shadow 0.2s ease, background-color 0.2s ease;
}
.sticky-tabs.is-stuck {
  background: #f1f5f9;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}
.sticky-tabs-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.sticky-tabs-inner .modern-year-tabs {
  flex: 1;
  min-width: 0;
}
/* Sticky hospital context pill */
.sticky-hosp-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.sticky-hosp-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 155px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.sticky-hosp-pill.is-clickable {
  cursor: pointer;
  border-style: solid;
  border-width: 1px;
  font-family: inherit;
  outline: none;
}
.sticky-hosp-pill.is-clickable:hover {
  filter: brightness(0.95);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
}
.sticky-hosp-pill.is-clickable:active {
  transform: scale(0.96);
}
.pill-icon {
  font-size: 12px;
  line-height: 1;
}
.pill-arrow {
  font-size: 10px;
  margin-left: 2px;
  opacity: 0.7;
}
.sticky-hosp-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pill-home {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}
.pill-other {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}
/* Fade-in/out transition */
.hosp-pill-fade-enter-active,
.hosp-pill-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.hosp-pill-fade-enter-from,
.hosp-pill-fade-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

/* Dropdown popper styling */
:deep(.sticky-hosp-popper),
:global(.sticky-hosp-popper) {
  border-radius: 14px !important;
  box-shadow: 0 12px 28px -4px rgba(15, 23, 42, 0.16), 0 8px 10px -6px rgba(15, 23, 42, 0.08) !important;
  border: 1px solid #e2e8f0 !important;
  padding: 6px 0 !important;
  min-width: 260px;
  max-width: 330px;
}
:global(.sticky-dd-header) {
  padding: 8px 14px 6px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
}
:global(.sticky-dd-item) {
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  padding: 4px 0;
  text-align: left;
}
:global(.sticky-dd-top) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
:global(.sticky-dd-name) {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:global(.sticky-dd-bottom) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}
:global(.sticky-dd-date) {
  font-size: 11px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
:global(.sticky-dd-check) {
  color: #2563eb;
  font-size: 14px;
  font-weight: 800;
}
:global(.sticky-hosp-popper .el-dropdown-menu__item.is-active) {
  background-color: #eff6ff !important;
  color: #2563eb !important;
}
:global(.sticky-hosp-popper .el-dropdown-menu__item:hover) {
  background-color: #f8fafc;
}

.modern-year-tabs :deep(.el-tabs__header) {
  margin: 0;
  border-bottom: 2px solid #e2e8f0;
}
.modern-year-tabs :deep(.el-tabs__item) {
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  padding: 0 16px;
}
.modern-year-tabs :deep(.el-tabs__item.is-active) { color: #2563eb; }
.modern-year-tabs :deep(.el-tabs__active-bar) {
  background-color: #2563eb;
  height: 3px;
  border-radius: 3px 3px 0 0;
}

/* ===== Same Hospital Intra-Year Visit Manager ===== */
.vsw-same-hosp-box {
  margin-bottom: 16px;
  padding: 14px;
  background: linear-gradient(180deg, #f0f7ff 0%, #f8fafc 100%);
  border: 1.5px solid #bfdbfe;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.05);
}
.same-hosp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}
.same-hosp-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.same-hosp-icon {
  font-size: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}
.same-hosp-titles {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.same-hosp-name {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.same-hosp-sub {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}
.same-hosp-count-tag {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  white-space: nowrap;
  flex-shrink: 0;
}
.same-hosp-visits-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.same-hosp-visit-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.same-hosp-visit-card:hover {
  border-color: #93c5fd;
  background: #fafcff;
}
.same-hosp-visit-card.is-active {
  border-color: #2563eb;
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.12);
}
.same-hosp-visit-body {
  flex: 1;
  min-width: 0;
}
.same-hosp-visit-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}
.visit-round-label {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}
.visit-latest-pill {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}
.visit-type-tag {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
}
.same-hosp-visit-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
}
.visit-date-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}
.visit-score-pill {
  font-size: 10px;
  font-weight: 600;
  color: #16a34a;
  background: #dcfce7;
  padding: 1px 6px;
  border-radius: 4px;
}
.same-hosp-compare-bar {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #cbd5e1;
}
.same-hosp-compare-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #93c5fd;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.06);
}
.same-hosp-compare-btn:hover {
  background: #eff6ff;
  border-color: #2563eb;
}
.sticky-dd-latest {
  font-size: 10px;
  color: #1d4ed8;
  font-weight: 700;
  margin-left: 3px;
}

/* Comparison Dialog */
:deep(.cmp-dialog-custom) {
  border-radius: 18px;
  overflow: hidden;
  max-width: 540px;
}
:deep(.cmp-dialog-custom .el-dialog__header) {
  margin-right: 0;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f1f5f9;
}
:deep(.cmp-dialog-custom .el-dialog__title) {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}
:deep(.cmp-dialog-custom .el-dialog__body) {
  padding: 16px 20px;
}
.cmp-dialog-banner {
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  margin-bottom: 14px;
}
.cmp-hosp-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.cmp-hosp-icon {
  font-size: 20px;
}
.cmp-hosp-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}
.cmp-hosp-sub {
  font-size: 11px;
  color: #64748b;
}
.cmp-dates-track {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #ffffff;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}
.cmp-track-box {
  display: flex;
  flex-direction: column;
}
.cmp-track-badge {
  font-size: 10px;
  color: #64748b;
  font-weight: 600;
}
.cmp-track-badge.is-latest {
  color: #1d4ed8;
  font-weight: 700;
}
.cmp-track-date {
  font-size: 11px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 1px;
}
.cmp-track-arrow {
  color: #94a3b8;
  display: flex;
  align-items: center;
}
.cmp-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 30px 0;
  color: #64748b;
  font-size: 13px;
}
.cmp-table-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  max-height: 380px;
  overflow-y: auto;
}
.cmp-table-head {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1.3fr;
  padding: 8px 12px;
  background: #f1f5f9;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}
.cmp-table-rows {
  display: flex;
  flex-direction: column;
}
.cmp-row {
  display: grid;
  grid-template-columns: 2fr 1.2fr 1.2fr 1.3fr;
  padding: 9px 12px;
  font-size: 12px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
}
.cmp-row:last-child {
  border-bottom: none;
}
.cmp-item-name {
  font-weight: 600;
  color: #1e293b;
  display: block;
  line-height: 1.3;
}
.cmp-item-unit {
  font-size: 10px;
  color: #94a3b8;
}
.cmp-val-text {
  font-weight: 600;
  color: #334155;
}
.cmp-val-text.status-bad {
  color: #ef4444;
}
.cmp-val-text.status-warn {
  color: #f59e0b;
}
.cmp-val-text.status-ok {
  color: #10b981;
}
.cmp-diff-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
}
.cmp-diff-tag.is-improved {
  background: #dcfce7;
  color: #15803d;
}
.cmp-diff-tag.is-worse {
  background: #fee2e2;
  color: #b91c1c;
}
.cmp-diff-tag.is-flat {
  background: #f1f5f9;
  color: #64748b;
}
.cmp-diff-dash {
  color: #cbd5e1;
}
.cmp-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

/* ===== Visit Switcher (Segmented Cards for multiple visits) ===== */
.vsw-box {
  margin-bottom: 16px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
}
.vsw-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 0 2px;
}
.vsw-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}
.vsw-hint {
  font-size: 11px;
  color: #64748b;
}
.vsw-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vsw-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  border-radius: 12px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.vsw-card:hover {
  border-color: #cbd5e1;
  background: #fcfdfe;
}
.vsw-card.is-active {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 100%);
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.1);
}
.vsw-card.is-other.is-active {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.1);
}
.vsw-card-radio {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.vsw-radio-ring {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: #ffffff;
}
.vsw-radio-ring.checked {
  border-color: #2563eb;
}
.vsw-card.is-other .vsw-radio-ring.checked {
  border-color: #d97706;
}
.vsw-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2563eb;
}
.vsw-card.is-other .vsw-radio-dot {
  background: #d97706;
}
.vsw-card-body {
  flex: 1;
  min-width: 0;
}
.vsw-hosp-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.vsw-hosp-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vsw-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}
.chip-home {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}
.chip-other {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}
.vsw-date-line {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #64748b;
  margin-top: 3px;
}
.vsw-viewing-badge {
  color: #2563eb;
  font-weight: 600;
  font-size: 10px;
  background: #dbeafe;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: auto;
}
.vsw-card.is-other .vsw-viewing-badge {
  color: #b45309;
  background: #fef3c7;
}

/* Dropdown mode (≥ 3 visits) */
.vsw-dropdown-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vsw-select {
  width: 100%;
}
.vsw-select :deep(.el-input__wrapper) {
  border-radius: 12px;
  box-shadow: 0 0 0 1.5px #e2e8f0 !important;
  padding: 4px 12px;
}
.vsw-select :deep(.el-input__wrapper):hover {
  box-shadow: 0 0 0 1.5px #93c5fd !important;
}
.vsw-select :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px #3b82f6 !important;
}
.vsw-opt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 0;
}
.vsw-opt-top {
  display: flex;
  align-items: center;
  gap: 6px;
}
.vsw-opt-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}
.vsw-opt-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
}
.vsw-selected-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  font-size: 12px;
  color: #1e40af;
}

/* Single visit banner */
.single-hosp-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 11px 13px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-left: 4px solid #3b82f6;
  border-radius: 12px;
}
.single-hosp-banner.is-other {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 80%);
}
.shb-icon {
  font-size: 20px;
  flex-shrink: 0;
}
.shb-body {
  flex: 1;
  min-width: 0;
}
.shb-line-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.shb-name {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shb-line-date {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

/* Cards */
.mobile-card {
  background: white;
  border-radius: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
}

/* Overview Card */
.overview-card { padding: 16px; }
.overview-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;
}
.overview-title { font-size: 15px; font-weight: 700; color: #1e293b; }
.status-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600;
}
.status-pill.green { background-color: #dcfce7; color: #16a34a; }

.overview-stats-mobile {
  display: flex; gap: 12px; margin-bottom: 16px;
}
.stat-box {
  flex: 1; border-radius: 12px; padding: 12px;
  display: flex; flex-direction: column; gap: 8px;
}
.green-box { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }
.orange-box { background: #fff7ed; color: #f59e0b; border: 1px solid #ffedd5; }
.stat-main { font-size: 20px; font-weight: 700; color: #1e293b; line-height: 1; }
.stat-sub { font-size: 12px; font-weight: normal; color: #64748b; }

/* Vitals Block */
.vitals-container { margin-bottom: 16px; }
.vitals-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.vitals-title { font-size: 15px; font-weight: 700; color: #1e293b; display: flex; align-items: center; }
.vitals-time { font-size: 13px; color: #64748b; }
.vitals-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.vital-card { background: #f8fafc; border-radius: 12px; padding: 12px; display: flex; align-items: center; gap: 12px; border: 1px solid #f1f5f9; }
.vital-icon-container { display: flex; justify-content: center; align-items: center; }
.vital-info { display: flex; flex-direction: column; }
.vital-label { font-size: 12px; color: #64748b; margin-bottom: 2px; }
.vital-val-row { display: flex; align-items: baseline; gap: 4px; }
.vital-val { font-size: 18px; font-weight: 700; }
.vital-unit { font-size: 11px; color: #94a3b8; }
.text-pink { color: #ec4899; }
.text-red { color: #ef4444; }

.date-footer {
  font-size: 13px; color: #64748b;
  display: flex; align-items: center; gap: 6px;
  justify-content: center;
  padding-top: 12px; border-top: 1px dashed #e2e8f0;
}

/* Horizontal Nav Scroll */
.nav-scroll-wrapper {
  margin: 0 -12px 24px -12px;
  padding: 0 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.nav-scroll-wrapper::-webkit-scrollbar { display: none; }
.nav-menu-hscroll {
  display: flex;
  gap: 16px;
  min-width: max-content;
  padding-bottom: 8px;
}
.nav-btn-mobile {
  display: flex; flex-direction: column; align-items: center;
  width: 64px; cursor: pointer;
}
.nav-icon-bg {
  width: 48px; height: 48px; border-radius: 14px;
  display: flex; justify-content: center; align-items: center;
  margin-bottom: 6px;
}
.blue-bg { background: #eff6ff; }
.red-bg { background: #fef2f2; }
.green-bg { background: #f0fdf4; }
.purple-bg { background: #faf5ff; }
.orange-bg { background: #fff7ed; }
.emoji-icon { font-size: 20px; }
.nav-text-mobile {
  font-size: 11px; font-weight: 600; color: #475569;
  text-align: center; line-height: 1.2;
}

/* Section Headers & Cards */
.section-card {
  scroll-margin-top: 80px;
}
.section-header {
  padding: 14px 16px;
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 700;
}
.blue-header { color: #3b82f6; background: #eff6ff; }
.red-header { color: #ef4444; background: #fef2f2; }
.green-header { color: #10b981; background: #f0fdf4; }
.summary-header { background: #10b981; color: white; }
.drop-icon { font-size: 16px; line-height: 1; }

/* Info Row Card (Physical) */
.info-row-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #f1f5f9;
}
.info-label { font-size: 13px; font-weight: 600; color: #334155; }
.dental-grid { display: flex; gap: 24px; text-align: right; }

/* Result Cards (CBC / Lab) */
.list-container {
  display: flex;
  flex-direction: column;
  padding: 0 16px 16px;
  gap: 12px;
}
.result-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}
.rc-header {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 8px;
}
.rc-name { font-size: 13px; font-weight: 600; color: #334155; }
.pill-badge {
  padding: 3px 8px; border-radius: 12px; font-size: 11px; font-weight: 600;
}
.pill-badge.green { background: #dcfce7; color: #16a34a; }
.pill-badge.yellow { background: #fef08a; color: #b45309; }
.sm-pill { padding: 2px 6px; font-size: 10px; }

.rc-value-group { display: flex; align-items: baseline; gap: 4px; margin-bottom: 4px; }
.rc-val { font-size: 20px; font-weight: 800; color: #1e293b; }
.rc-unit { font-size: 12px; color: #64748b; }
.rc-ref { font-size: 11px; color: #94a3b8; }

.result-card.compact .rc-body.horizontal {
  display: flex; justify-content: space-between; align-items: center;
}
.rc-val-sm { font-size: 16px; font-weight: 700; color: #1e293b; }
.rc-ref-sm { font-size: 11px; color: #94a3b8; }

.card-footer-btn {
  padding: 12px; background: #f8fafc;
  display: flex; justify-content: center; align-items: center; gap: 4px;
  font-size: 13px; font-weight: 600; color: #3b82f6; border-top: 1px solid #f1f5f9;
}

/* Highlight Lab Card */
.lab-tabs-mobile :deep(.el-radio-button) { flex: 1; }
.lab-tabs-mobile :deep(.el-radio-button__inner) {
  width: 100%; border: 1px solid #e2e8f0; border-left: none;
  background: white; color: #64748b; padding: 8px 0; font-size: 13px;
}
.lab-tabs-mobile :deep(.el-radio-button:first-child .el-radio-button__inner) {
  border-left: 1px solid #e2e8f0; border-radius: 8px 0 0 8px;
}
.lab-tabs-mobile :deep(.el-radio-button:last-child .el-radio-button__inner) {
  border-radius: 0 8px 8px 0;
}
.lab-tabs-mobile :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
  background: #3b82f6; color: white; border-color: #3b82f6; box-shadow: none;
}

.highlight-lab-card {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px;
}
.hl-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.hl-title { font-size: 14px; font-weight: 700; color: #334155; }
.hl-value-area { display: flex; align-items: baseline; gap: 6px; margin-bottom: 12px; }
.hl-val { font-size: 32px; font-weight: 800; color: #1e293b; line-height: 1; }
.hl-unit { font-size: 14px; color: #64748b; }
.hl-ref { border-top: 1px dashed #cbd5e1; padding-top: 12px; display: flex; justify-content: space-between; align-items: center; }

/* Mini Bar Chart */
.mini-bar-chart {
  display: flex; flex-direction: column; gap: 8px;
}
.bar-row {
  display: flex; align-items: center; gap: 12px;
}
.bar-lbl { font-size: 12px; font-weight: 600; color: #1e293b; width: 32px; }
.bar-track {
  flex: 1; height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden;
}
.bar-fill {
  height: 100%; background: #3b82f6; border-radius: 4px; transition: width 0.3s ease;
}
.bg-gray { background: #f1f5f9; }
.bg-gray-fill { background: #94a3b8; }
.bar-val-txt { font-size: 12px; font-weight: 600; width: 28px; text-align: right; }

.divider-line { height: 1px; background: #e2e8f0; }

/* Summary List */
.mobile-summary-list {
  list-style: none; padding: 0; margin: 0;
}
.mobile-summary-list li {
  display: flex; align-items: flex-start; gap: 8px;
  font-size: 13px; color: #334155; padding: 10px 0; border-bottom: 1px solid #f1f5f9;
}
.mobile-summary-list li:last-child { border-bottom: none; }
.mobile-summary-list li .el-icon { margin-top: 2px; }
.warning-li { flex-wrap: wrap; }

.tip-box {
  background: #f0fdf4; border-radius: 12px; padding: 12px;
  display: flex; gap: 10px; align-items: flex-start;
}
.tip-icon { color: #16a34a; }
.tip-title { font-size: 13px; font-weight: 700; color: #16a34a; margin-bottom: 2px; }
.tip-desc { font-size: 12px; color: #475569; line-height: 1.4; }

.bottom-spacer { height: 40px; }

/* Back to Top */
.backtop-icon {
  height: 100%;
  width: 100%;
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border-radius: 50%;
  text-align: center;
  line-height: 40px;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

/* Category List Styles */
.category-section-title {
  font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 12px; margin-left: 4px;
}
.category-list {
  display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px;
}
.cat-card-container {
  background: white; border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  overflow: hidden;
  transition: all 0.2s ease;
}
.cat-card-container.is-expanded {
  box-shadow: 0 6px 16px rgba(0,0,0,0.06);
  margin-top: 4px; margin-bottom: 4px;
}
.cat-card {
  padding: 14px 16px;
  display: flex; align-items: center; gap: 12px;
  border-left: 4px solid transparent; cursor: pointer;
  user-select: none;
}

.border-bad { border-left-color: #ef4444; }
.border-warn { border-left-color: #f59e0b; }
.border-ok { border-left-color: #10b981; }

.cat-icon-container { display: flex; justify-content: center; align-items: center; width: 44px; height: 44px; border-radius: 12px; background: #f8fafc; }
.cat-emoji { font-size: 24px; }

.cat-info { flex: 1; min-width: 0; }
.cat-name { font-size: 14px; font-weight: 700; color: #1e293b; margin-bottom: 2px; }
.cat-summary-text { font-size: 11px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.cat-status-badge { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.bg-bad { background: #fee2e2; color: #ef4444; }
.bg-warn { background: #fef3c7; color: #d97706; }
.bg-ok { background: #dcfce7; color: #10b981; }

.cat-chevron { color: #94a3b8; font-size: 16px; margin-left: 4px; transition: transform 0.3s ease; }
.cat-chevron.is-rotated { transform: rotate(90deg); color: #3b82f6; }

/* Expanded Details */
.cat-details {
  border-top: 1px solid #f1f5f9;
  background-color: #fafbfc;
}
.cat-details-inner {
  padding: 16px;
}
.cat-tests-list {
  display: flex; flex-direction: column; gap: 12px;
}
.test-item {
  display: flex; justify-content: space-between; align-items: center;
  padding-bottom: 12px; border-bottom: 1px dashed #e2e8f0;
}
.test-item:last-of-type { border-bottom: none; padding-bottom: 8px; }
.test-info { flex: 1; min-width: 0; padding-right: 12px; }
.test-name { font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 2px; }
.test-desc { font-size: 11.5px; color: #64748b; margin-bottom: 4px; line-height: 1.35; word-break: break-word; }
.test-ref { font-size: 11px; color: #94a3b8; }
.test-val { font-size: 16px; font-weight: 800; margin-right: 4px; }
.test-unit { font-size: 11px; color: #64748b; }
.test-status-text { font-size: 11px; font-weight: 600; margin-top: 2px; }

.text-bad { color: #ef4444; }
.text-warn { color: #f59e0b; }
.text-ok { color: #10b981; }
.text-sensitive { color: #ea580c; font-size: 13px !important; font-weight: 600 !important; }

.ti-chip.bg-sensitive { background: #ffedd5; color: #c2410c; }
.test-item.ti-sensitive { border-left-color: #ea580c !important; }

.empty-tests { font-size: 12px; color: #94a3b8; text-align: center; padding: 10px 0; }

/* Top Summary Card */
.summary-top-card {
  background: white; border-radius: 16px; padding: 16px;
  display: flex; align-items: center; gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border: 1px solid #fee2e2;
}
.stc-icon { display: flex; justify-content: center; align-items: center; width: 48px; height: 48px; background: #fef2f2; border-radius: 12px; }
.stc-content { flex: 1; }
.stc-title { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 2px; }
.stc-desc { font-size: 13px; color: #64748b; }

/* Segmented Control */
.filter-segmented-control {
  display: flex; background: white; border-radius: 12px; padding: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}
.segment-item {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 8px 4px; border-radius: 8px; cursor: pointer; transition: all 0.2s;
}
.segment-item.active { background: #f1f5f9; box-shadow: inset 0 1px 3px rgba(0,0,0,0.05); }
.seg-label { font-size: 12px; font-weight: 600; color: #64748b; }
.seg-count { font-size: 16px; font-weight: 800; color: #1e293b; line-height: 1.2; margin-top: 2px; }
.segment-divider { width: 1px; background: #e2e8f0; margin: 8px 0; }

/* Category List */
.category-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.cat-card-container {
  background: white; border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  overflow: hidden; transition: all 0.2s ease;
}
.cat-card-container.is-expanded { box-shadow: 0 6px 16px rgba(0,0,0,0.06); margin-top: 4px; margin-bottom: 4px; border: 1px solid #e2e8f0; }
.cat-card {
  padding: 14px 16px; display: flex; align-items: center; gap: 12px;
  border-left: 4px solid transparent; cursor: pointer; user-select: none;
}
.border-bad { border-left-color: #ef4444; }
.border-warn { border-left-color: #f59e0b; }
.border-ok { border-left-color: #10b981; }

.cat-icon-container { display: flex; justify-content: center; align-items: center; width: 44px; height: 44px; border-radius: 12px; background: #f8fafc; }
.cat-emoji { font-size: 24px; }

.cat-info { flex: 1; min-width: 0; }
.cat-name { font-size: 15px; font-weight: 700; color: #1e293b; margin-bottom: 2px; }
.cat-summary-text { font-size: 13px; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.cat-chevron { color: #94a3b8; font-size: 16px; margin-left: 4px; transition: transform 0.3s ease; }
.cat-chevron.is-rotated { transform: rotate(90deg); color: #3b82f6; }

/* Expanded Details */
.cat-details { border-top: 1px solid #f1f5f9; background-color: #fafbfc; }
.cat-details-inner { padding: 16px; }
.cat-tests-list { display: flex; flex-direction: column; gap: 16px; }
.test-item { display: flex; flex-direction: column; padding-bottom: 16px; border-bottom: 1px dashed #e2e8f0; }
.test-item:last-of-type { border-bottom: none; padding-bottom: 0; }
.test-name { font-size: 14px; font-weight: 600; color: #334155; }
.test-row-main { display: flex; align-items: baseline; gap: 12px; }
.test-val-group { display: flex; align-items: baseline; gap: 4px; }
.test-val { font-size: 24px; font-weight: 800; }
.test-unit { font-size: 13px; color: #64748b; }
.test-status-text { font-size: 14px; font-weight: 700; display: flex; align-items: center; gap: 4px; }
.status-dot { font-size: 10px; }
.test-ref { font-size: 12px; color: #94a3b8; }

.text-bad { color: #ef4444; }
.text-warn { color: #f59e0b; }
.text-ok { color: #10b981; }
.text-sensitive { color: #ea580c; font-size: 13px !important; font-weight: 600 !important; }

.ti-chip.bg-sensitive { background: #ffedd5; color: #c2410c; }
.test-item.ti-sensitive { border-left-color: #ea580c !important; }
.dot-bad { color: #ef4444; }
.dot-warn { color: #f59e0b; }
.dot-ok { color: #10b981; }

.cat-action-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px; border-radius: 12px; background: #eff6ff; color: #2563eb;
  font-size: 13px; font-weight: 600; cursor: pointer;
  border: 1px solid #bfdbfe;
}
.cat-action-row:active { background: #dbeafe; }

.empty-tests { font-size: 13px; color: #94a3b8; text-align: center; padding: 16px 0; }

/* ===== Redesigned test detail ===== */
.cat-card { padding: 16px; min-height: 64px; }
.cat-card:active { background: #f8fafc; }
.cat-summary-text { white-space: normal; }
.cat-status-badge { flex-shrink: 0; }
.cat-details-inner { padding: 12px; }
.cat-tests-list { gap: 10px; }
.test-item {
  background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #10b981;
  border-radius: 12px; padding: 12px 14px; gap: 4px;
}
.test-item:last-of-type { padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
.test-item.ti-warn { border-left-color: #f59e0b; background: #fffbeb; }
.test-item.ti-bad { border-left-color: #ef4444; background: #fef2f2; }
.ti-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.ti-chip { flex-shrink: 0; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.test-val { font-size: 28px; line-height: 1.1; }
.test-ref { font-size: 12px; color: #64748b; }

.gauge { margin: 6px 0 2px; }
.gauge-track { position: relative; height: 8px; border-radius: 4px; background: #e2e8f0; }
.gauge-zone { position: absolute; top: 0; bottom: 0; background: #86efac; border-radius: 4px; }
.gauge-marker {
  position: absolute; top: -4px; width: 16px; height: 16px; border-radius: 50%;
  transform: translateX(-50%); border: 3px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.3);
}
.mk-ok { background: #10b981; }
.mk-warn { background: #f59e0b; }
.mk-bad { background: #ef4444; }
.gauge-legend { font-size: 10.5px; color: #94a3b8; margin-top: 6px; }

.cat-action-row { padding: 14px; font-size: 14px; background: #2563eb; color: #fff; border-color: #2563eb; }
.cat-action-row:active { background: #1d4ed8; }

/* ===== Balanced left/right alignment ===== */
.cat-tests-list, .test-item { align-items: stretch; text-align: left; }
.cat-card { text-align: left; }
.cat-info { text-align: left; }
.test-item { gap: 8px; padding: 14px; }
.ti-top { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.ti-top .test-name { flex: 1; text-align: left; font-size: 15px; font-weight: 700; }
.ti-mid {
  display: flex; justify-content: space-between; align-items: flex-end;
  width: 100%; gap: 12px;
}
.ti-mid .test-val-group { display: flex; align-items: baseline; gap: 6px; }
.ti-ref { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.ti-ref-label { font-size: 11px; color: #94a3b8; }
.ti-ref-val { font-size: 14px; font-weight: 600; color: #334155; }
.gauge { width: 100%; margin: 8px 0 0; }
.gauge-track { width: 100%; height: 10px; border-radius: 5px; }
.gauge-marker { top: -3px; }
.gauge-legend {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 11px; color: #94a3b8; margin-top: 8px;
}
.cat-action-row { width: 100%; box-sizing: border-box; text-align: left; }

/* ===== Patient-friendly meaning row ===== */
.ti-meaning {
  display: flex; align-items: flex-start; gap: 6px;
  margin-top: 10px; padding: 8px 10px; border-radius: 8px;
  font-size: 13px; line-height: 1.5; font-weight: 500;
}
.meaning-icon { font-size: 14px; flex-shrink: 0; line-height: 1.5; }
.meaning-ok   { background: #f0fdf4; color: #166534; }
.meaning-warn { background: #fffbeb; color: #92400e; }
.meaning-bad  { background: #fef2f2; color: #991b1b; }

.test-item.is-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.test-item.is-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}
.test-name-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.test-subtitle {
  font-size: 11px;
  color: #64748b;
  font-weight: 400;
}

/* ===== IN / OUT Service Setting UI ===== */
.setting-filter-bar {
  display: flex;
  gap: 8px;
  margin: 10px 0 14px 0;
  overflow-x: auto;
  padding-bottom: 2px;
}
.setting-filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}
.setting-filter-btn.active {
  background: #2563eb;
  color: #ffffff;
  border-color: #2563eb;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
}
.visit-setting-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  line-height: 1.2;
}
.visit-setting-pill.pill-in {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}
.visit-setting-pill.pill-out {
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}
.visit-setting-loc {
  font-size: 11.5px;
  color: #475569;
  margin-top: 3px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.report-id-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  color: #64748b;
  margin-left: 6px;
}
</style>
